import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  Card,
  CardContent,
  Grid,
  Alert,
  CircularProgress,
  Divider,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import api from "../api";

// ══════════════════════════════════════════════════════════════
// CONSTANTS
// ══════════════════════════════════════════════════════════════

const PRIMARY = "#005E60";
const DARK = "#1a1a2e";

const COMPONENT_TYPES = ["VFD", "TRANS", "MOTOR", "GEN", "SWBDS", "UPS", "DBR"];

const DIMENSION_FIELDS = new Set([
  "max_height_mm",
  "max_width_mm",
  "max_depth_mm",
  "max_weight_kg",
  "floor_loading_kn_m2",
]);

const DIMENSION_LABELS = {
  max_height_mm: "Max Height — Do Not Exceed (mm)",
  max_width_mm: "Max Width — Do Not Exceed (mm)",
  max_depth_mm: "Max Depth — Do Not Exceed (mm)",
  max_weight_kg: "Max Weight — Do Not Exceed (kg)",
  floor_loading_kn_m2: "Max Floor Loading — Do Not Exceed (kN/m²)",
};

const COMPONENT_SPECS = {
  VFD: [
    "input_voltage",
    "output_voltage",
    "power_rating_kw",
    "input_frequency",
    "output_frequency",
    "dc_bus_voltage",
    "input_current_a",
    "output_current_a",
    "switching_frequency_khz",
    "efficiency_percent",
    "power_factor",
    "harmonic_filter",
    "harmonic_distortion_thd",
    "cooling_method",
    "enclosure_rating",
    "insulation_class",
    "overload_capacity",
    "braking_method",
    "protection_functions",
    "communication_protocol",
    "mounting_arrangement",
    "shock_standard",
    "emc_standard",
    "max_height_mm",
    "max_width_mm",
    "max_depth_mm",
    "max_weight_kg",
    "floor_loading_kn_m2",
    "special_requirements",
  ],
  TRANS: [
    "primary_voltage",
    "secondary_voltage",
    "power_rating_kva",
    "frequency",
    "vector_group",
    "impedance_voltage_percent",
    "no_load_losses_w",
    "full_load_losses_w",
    "no_load_current_percent",
    "insulation_class",
    "temperature_rise_k",
    "cooling_type",
    "enclosure_rating",
    "tapping_range",
    "turns_ratio",
    "primary_current_a",
    "secondary_current_a",
    "fault_current_contribution_ka",
    "short_circuit_withstand_ka",
    "short_circuit_duration_s",
    "winding_configuration",
    "partial_discharge_pc",
    "shock_standard",
    "emc_standard",
    "max_height_mm",
    "max_width_mm",
    "max_depth_mm",
    "max_weight_kg",
    "floor_loading_kn_m2",
    "special_requirements",
  ],
  MOTOR: [
    "rated_power_kw",
    "rated_voltage",
    "frequency",
    "rated_speed_rpm",
    "synchronous_speed_rpm",
    "number_of_poles",
    "rated_current_a",
    "starting_current_a",
    "power_factor",
    "efficiency_percent",
    "efficiency_class",
    "insulation_class",
    "temperature_rise_k",
    "enclosure_rating",
    "mounting_type",
    "frame_size",
    "bearing_type",
    "cooling_method",
    "starting_method",
    "service_factor",
    "duty_cycle",
    "locked_rotor_torque_percent",
    "breakdown_torque_percent",
    "moment_of_inertia_kgm2",
    "shock_standard",
    "emc_standard",
    "max_height_mm",
    "max_width_mm",
    "max_depth_mm",
    "max_weight_kg",
    "floor_loading_kn_m2",
    "special_requirements",
  ],
  GEN: [
    "rated_power_kva",
    "rated_power_kw",
    "rated_voltage",
    "frequency",
    "power_factor",
    "speed_rpm",
    "number_of_poles",
    "rated_current_a",
    "efficiency_percent",
    "insulation_class",
    "temperature_rise_k",
    "enclosure_rating",
    "excitation_type",
    "avr_type",
    "voltage_regulation_percent",
    "transient_voltage_dip_percent",
    "frequency_regulation_percent",
    "subtransient_reactance_xd",
    "transient_reactance_xd",
    "synchronous_reactance_xd",
    "short_circuit_ratio",
    "fault_current_contribution_ka",
    "parallel_operation",
    "cooling_method",
    "mounting_arrangement",
    "shock_standard",
    "emc_standard",
    "max_height_mm",
    "max_width_mm",
    "max_depth_mm",
    "max_weight_kg",
    "floor_loading_kn_m2",
    "special_requirements",
  ],
  SWBDS: [
    "bus_voltage",
    "bus_current_rating_a",
    "frequency",
    "fault_level_ka",
    "fault_duration_s",
    "number_of_incoming_feeders",
    "incoming_breaker_rating_a",
    "number_of_outgoing_feeders",
    "busbar_material",
    "busbar_rating_a",
    "busbar_configuration",
    "enclosure_rating",
    "protection_standard",
    "form_of_separation",
    "breaking_capacity_ka",
    "arc_flash_mitigation",
    "protection_relay_type",
    "metering",
    "neutral_earthing",
    "cooling_method",
    "mounting_arrangement",
    "shock_standard",
    "emc_standard",
    "max_height_mm",
    "max_width_mm",
    "max_depth_mm",
    "max_weight_kg",
    "floor_loading_kn_m2",
    "special_requirements",
  ],
  UPS: [
    "input_voltage",
    "output_voltage",
    "power_rating_kva",
    "power_rating_kw",
    "frequency",
    "input_frequency_range",
    "output_frequency_regulation",
    "output_voltage_regulation",
    "efficiency_percent",
    "topology",
    "battery_type",
    "battery_autonomy_min",
    "battery_voltage",
    "battery_recharge_time_h",
    "bypass_rating_a",
    "static_bypass",
    "maintenance_bypass",
    "overload_capacity",
    "harmonic_distortion_thd",
    "power_factor_input",
    "power_factor_output",
    "enclosure_rating",
    "insulation_class",
    "cooling_method",
    "mounting_arrangement",
    "communication_protocol",
    "shock_standard",
    "emc_standard",
    "max_height_mm",
    "max_width_mm",
    "max_depth_mm",
    "max_weight_kg",
    "floor_loading_kn_m2",
    "special_requirements",
  ],
  DBR: [
    "dc_bus_voltage",
    "peak_braking_power_kw",
    "continuous_braking_power_kw",
    "braking_duration_peak_s",
    "braking_duration_continuous",
    "duty_cycle",
    "resistance_value_ohm",
    "resistance_tolerance",
    "peak_current_a",
    "continuous_current_a",
    "energy_per_cycle_kwh",
    "insulation_class",
    "thermal_trip_temperature_c",
    "thermal_trip_reset",
    "temperature_monitoring",
    "number_of_resistor_banks",
    "resistor_element_material",
    "enclosure_rating",
    "cooling_method",
    "mounting_arrangement",
    "connected_vfd_tag",
    "connected_motor_tag",
    "shock_standard",
    "emc_standard",
    "max_height_mm",
    "max_width_mm",
    "max_depth_mm",
    "max_weight_kg",
    "floor_loading_kn_m2",
    "special_requirements",
  ],
};

const COMPONENT_COLORS = {
  VFD: { bg: "#005E60", light: "#e6f4f4" },
  TRANS: { bg: "#007b7d", light: "#e6f5f5" },
  MOTOR: { bg: "#00843d", light: "#e6f5ec" },
  GEN: { bg: "#c47a00", light: "#fdf3e3" },
  SWBDS: { bg: "#5c35a0", light: "#f0eafa" },
  UPS: { bg: "#b00033", light: "#fce8ed" },
  DBR: { bg: "#0077b6", light: "#e6f2fb" },
};

// ══════════════════════════════════════════════════════════════
// HELPERS
// ══════════════════════════════════════════════════════════════

const formatLabel = (key) =>
  key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const formatDimLabel = (key) => DIMENSION_LABELS[key] || formatLabel(key);
const isDimField = (key) => DIMENSION_FIELDS.has(key);

const sectionStyle = {
  borderRadius: "12px",
  border: "1px solid #e0e0e0",
  marginBottom: "1.5rem",
};

const fieldStyle = {
  "& label.Mui-focused": { color: PRIMARY },
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    "&.Mui-focused fieldset": { borderColor: PRIMARY },
  },
};

const dimFieldStyle = {
  "& label.Mui-focused": { color: "#b45309" },
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    backgroundColor: "#fffdf5",
    "&.Mui-focused fieldset": { borderColor: "#b45309" },
  },
};

// ══════════════════════════════════════════════════════════════
// COMPONENT
// ══════════════════════════════════════════════════════════════

function AddComponent() {
  const { id: projectId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [componentType, setComponentType] = useState("");
  const [tagNumber, setTagNumber] = useState("");
  const [specs, setSpecs] = useState({});

  const handleTypeChange = (e) => {
    setComponentType(e.target.value);
    setSpecs({});
    setError("");
  };

  const handleSpecChange = (e) =>
    setSpecs((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!componentType) {
      setError("Please select a component type.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      await api.post("/components/", {
        project_id: parseInt(projectId, 10),
        component_type: componentType,
        tag_number: tagNumber.trim() || null,
        // ── Strip empty strings so DB gets clean nulls ────────
        specifications: Object.fromEntries(
          Object.entries(specs).filter(([, v]) => v !== "" && v !== null),
        ),
      });
      navigate(`/project/${projectId}`);
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      if (
        detail.toLowerCase().includes("unique") ||
        detail.toLowerCase().includes("duplicate")
      ) {
        setError(
          `Tag number "${tagNumber}" already exists in this project. Please use a unique tag.`,
        );
      } else {
        setError("Failed to add component. Please try again.");
      }
      setLoading(false);
    }
  };

  const specFields = COMPONENT_SPECS[componentType] || [];
  const color = COMPONENT_COLORS[componentType] || {
    bg: PRIMARY,
    light: "#e6f4f4",
  };

  // ── Split spec fields into regular and dimension groups ───
  const regularFields = specFields.filter(
    (f) => !isDimField(f) && f !== "special_requirements",
  );
  const dimFields = specFields.filter((f) => isDimField(f));
  const hasSpecialReq = specFields.includes("special_requirements");

  return (
    <Box>
      {/* ── Breadcrumb ────────────────────────────────────── */}
      <Typography
        variant="body2"
        color="text.secondary"
        onClick={() => navigate(`/project/${projectId}`)}
        sx={{ mb: "0.5rem", cursor: "pointer", "&:hover": { color: PRIMARY } }}
      >
        ← Back to Project
      </Typography>

      {/* ── Page title ────────────────────────────────────── */}
      <Box sx={{ mb: "1.5rem" }}>
        <Typography variant="h4" sx={{ color: DARK, fontWeight: "bold" }}>
          Add Component
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Select a component type and enter its known specifications. Leave
          fields blank — the AI fill will complete them automatically.
        </Typography>
      </Box>

      {/* ── Error banner ──────────────────────────────────── */}
      {error && (
        <Alert
          severity="error"
          onClose={() => setError("")}
          sx={{ mb: "1rem", borderRadius: "8px" }}
        >
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        {/* ── Component Type & Tag ──────────────────────── */}
        <Card elevation={0} sx={sectionStyle}>
          <Box
            sx={{
              backgroundColor: componentType ? color.bg : PRIMARY,
              color: "white",
              padding: "0.75rem 1.25rem",
              borderRadius: "11px 11px 0 0",
              fontWeight: "bold",
              fontSize: "0.95rem",
              letterSpacing: 0.5,
              transition: "background-color 0.2s ease",
            }}
          >
            🔧 Component Type
          </Box>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  required
                  label="Component Type"
                  value={componentType}
                  onChange={handleTypeChange}
                  sx={fieldStyle}
                >
                  {COMPONENT_TYPES.map((t) => (
                    <MenuItem key={t} value={t}>
                      {t}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Tag Number (e.g. VFD-001)"
                  value={tagNumber}
                  onChange={(e) => setTagNumber(e.target.value)}
                  sx={fieldStyle}
                  helperText="Leave blank to auto-assign"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* ── Dynamic Spec Fields ───────────────────────── */}
        {componentType && (
          <Card elevation={0} sx={sectionStyle}>
            <Box
              sx={{
                backgroundColor: color.bg,
                color: "white",
                padding: "0.75rem 1.25rem",
                borderRadius: "11px 11px 0 0",
                fontWeight: "bold",
                fontSize: "0.95rem",
                letterSpacing: 0.5,
              }}
            >
              ⚡ {componentType} Specifications
              <span
                style={{
                  fontWeight: "normal",
                  fontSize: "0.8rem",
                  marginLeft: "0.5rem",
                  opacity: 0.8,
                }}
              >
                — fill in what you know, AI will complete the rest
              </span>
            </Box>

            <CardContent>
              <Grid container spacing={2}>
                {/* ── Regular spec fields ────────────────── */}
                {regularFields.map((field) => (
                  <Grid item xs={12} sm={6} key={field}>
                    <TextField
                      fullWidth
                      label={formatLabel(field)}
                      name={field}
                      value={specs[field] || ""}
                      onChange={handleSpecChange}
                      sx={fieldStyle}
                    />
                  </Grid>
                ))}

                {/* ── Dimension envelope divider ──────────── */}
                {dimFields.length > 0 && (
                  <Grid item xs={12}>
                    <Divider sx={{ my: "0.5rem" }} />
                    <Box
                      sx={{
                        backgroundColor: "#fff8e1",
                        border: "1px solid #ffe082",
                        borderRadius: "8px",
                        padding: "0.6rem 1rem",
                        mt: "0.25rem",
                        mb: "0.5rem",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: "bold", color: "#b45309" }}
                      >
                        📐 Physical Envelope — Do Not Exceed
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#78716c" }}>
                        Leave blank if no dimensional restrictions apply. Vendor
                        will confirm actual dimensions at tender stage.
                      </Typography>
                    </Box>
                  </Grid>
                )}

                {/* ── Dimension fields ────────────────────── */}
                {dimFields.map((field) => (
                  <Grid item xs={12} sm={6} key={field}>
                    <TextField
                      fullWidth
                      label={formatDimLabel(field)}
                      name={field}
                      value={specs[field] || ""}
                      onChange={handleSpecChange}
                      placeholder="Leave blank if no restriction"
                      sx={dimFieldStyle}
                    />
                  </Grid>
                ))}

                {/* ── Special requirements — always last, full width ── */}
                {hasSpecialReq && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Special Requirements"
                      name="special_requirements"
                      value={specs["special_requirements"] || ""}
                      onChange={handleSpecChange}
                      placeholder="Any special design, installation, or performance requirements..."
                      sx={fieldStyle}
                    />
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* ── Action buttons ────────────────────────────── */}
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={loading || !componentType}
            sx={{
              backgroundColor: componentType ? color.bg : PRIMARY,
              borderRadius: "8px",
              padding: "0.75rem 2rem",
              fontWeight: "bold",
              textTransform: "none",
              "&:hover": {
                backgroundColor: componentType ? color.bg : "#004749",
                filter: "brightness(0.9)",
              },
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "➕ Add Component"
            )}
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={() => navigate(`/project/${projectId}`)}
            sx={{
              borderColor: PRIMARY,
              color: PRIMARY,
              borderRadius: "8px",
              padding: "0.75rem 2rem",
              textTransform: "none",
              "&:hover": { backgroundColor: "#e6f4f4" },
            }}
          >
            Cancel
          </Button>
        </Box>
      </form>
    </Box>
  );
}

export default AddComponent;
